import { useIntl } from 'react-intl';
import { Alert, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { Formik, Form as FormikForm } from 'formik';

import { useAxios } from 'configs/requests';
import { Input, Select } from 'components/Forms';
import { Loading, Error } from 'components/Fetch';
import { getDeviceConditionOptions, smartMeterManufacturerNames } from 'constants/constants';
import { ShippingSpeed } from 'constants/enums';
import {
  addressesAreEqual,
  isAddressValid,
  shippingAddressToForm,
  toDeviceOrderShippingInfo
} from 'utils/Address';
import { isAmazonFulfilled } from 'utils/Devices';
import type { IShippingInfo } from 'pages/Dashboard/Onboard/Onboard';
import ShippingAddressForm from 'pages/Dashboard/Onboard/Steps/ShippingAddressForm';

import ShippingSpeedField from './ShippingSpeedField';

interface IPatientAddressDto {
  streetAddressLine1: string;
  streetAddressLine2?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  countryCode: string;
}

interface ILastShippingAddress extends IPatientAddressDto {
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
}

interface IGetPatientResponse {
  uid: string;
  firstName?: string;
  lastName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  address?: IPatientAddressDto;
  lastShippingAddress?: ILastShippingAddress | null;
}

interface IReplaceDeviceForm {
  condition: string | null;
  imei: string | null;
  trackingNumber: string | null;
  shippingInfo: IShippingInfo;
  replacementDeviceOrderingId: string | null;
  shippingSpeed: ShippingSpeed;
  shippingSpeedEstimate: IDeliveryWindow | null;
}

interface IReplaceDevicePayload {
  condition: string | null;
  imei?: string | null;
  trackingNumber?: string | null;
  shippingInfo?: IDeviceOrderShippingInfo;
  shippingAddressIsPatientAddress?: boolean;
  replacementDeviceOrderingId: string;
  /** Only when the replacement SKU ships through Amazon. */
  shippingSpeed?: ShippingSpeedValue;
}

interface IReplaceDeviceModalProps {
  device: IDeviceListItem;
  onClose: () => void;
  onSuccess: () => void;
}

const ReplaceDeviceModal = ({ device, onClose, onSuccess }: IReplaceDeviceModalProps) => {
  const { formatMessage } = useIntl();
  const isSmartMeter = smartMeterManufacturerNames.includes(
    device.deviceOrdering?.manufacturer ?? ''
  );

  const [{ data: patientData, loading: loadingPatient }] = useAxios<IGetPatientResponse>(
    {
      url: `/clinician-gateway/patient/${device.assignedUid}`,
      method: 'GET'
    },
    { useCache: false }
  );

  const [{ data: replacementOptions, loading: loadingReplacementOptions }] = useAxios<
    IReplacementOption[]
  >(
    {
      url: `/clinician-gateway/device/${device.deviceId}/replacement-options`,
      method: 'GET'
    },
    { useCache: false }
  );

  const skuOptions: ISelectOption[] = (replacementOptions ?? []).map((option) => ({
    value: option.deviceOrderingId,
    label: `${option.sku} · ${formatMessage({ id: option.displayName })}`
  }));

  const currentDeviceOrderingId =
    device.deviceOrdering?.parent?.deviceOrderingId ?? device.deviceOrdering?.deviceOrderingId;
  const currentSkuOption = (replacementOptions ?? []).find(
    (option) => option.deviceOrderingId === currentDeviceOrderingId
  );

  /**
   * Who ships the chosen replacement: the replacement option says so; for the device's own
   * ordering the device record is the fallback. Unknown means no shipping-speed section rather
   * than a guess. Nothing is fetched into the shared catalogue slice here: that slice feeds the
   * Devices filters and must not be narrowed to one group by opening this modal.
   */
  const shippingProviderFor = (deviceOrderingId: string | null): string | undefined => {
    if (!deviceOrderingId) return undefined;
    const option = replacementOptions?.find((o) => o.deviceOrderingId === deviceOrderingId);
    if (option?.shippingProvider) return option.shippingProvider;
    if (deviceOrderingId === currentDeviceOrderingId)
      return device.deviceOrdering?.shippingProvider;
    return undefined;
  };

  const po =
    device.purchaseOrder && device.purchaseOrder.orderType !== 'bulk'
      ? device.purchaseOrder
      : undefined;
  const lastShip = patientData?.lastShippingAddress;

  const shippingContact = lastShip
    ? {
        firstName: lastShip.firstName ?? '',
        lastName: lastShip.lastName ?? '',
        email: lastShip.email ?? '',
        phoneNumber: lastShip.phoneNumber ?? ''
      }
    : {
        firstName: po?.name ?? '',
        lastName: '',
        email: po?.email ?? '',
        phoneNumber: po?.phoneNumber ?? ''
      };

  const patientContact = {
    firstName: patientData?.firstName ?? '',
    lastName: patientData?.lastName ?? '',
    email: patientData?.emailAddress ?? '',
    phoneNumber: patientData?.phoneNumber ?? ''
  };

  const homeAddress = shippingAddressToForm(patientData?.address);
  const poAddress = shippingAddressToForm(po?.address);
  const shippingAddress = isAddressValid(poAddress) ? poAddress : shippingAddressToForm(lastShip);

  const homeCandidate: IShippingInfo = {
    ...patientContact,
    address: homeAddress,
    useAsPatientAddress: false
  };
  const shippingCandidate: IShippingInfo = {
    ...shippingContact,
    address: shippingAddress,
    useAsPatientAddress: false
  };

  const shippingValid = !!isAddressValid(shippingAddress);
  const showAddressToggle =
    !!isAddressValid(homeAddress) &&
    shippingValid &&
    !addressesAreEqual(homeAddress, shippingAddress);

  const initialValues: IReplaceDeviceForm = {
    condition: null,
    imei: '',
    trackingNumber: '',
    shippingInfo: shippingValid ? shippingCandidate : homeCandidate,
    replacementDeviceOrderingId: currentSkuOption?.deviceOrderingId ?? null,
    shippingSpeed: ShippingSpeed.STANDARD,
    shippingSpeedEstimate: null
  };

  const conditionOptions = getDeviceConditionOptions().slice(1, 3); // keep only faulty and broken

  const [{ loading, error }, post] = useAxios(
    {
      url: `/clinician-gateway/device/${device.deviceId}/replace`,
      method: 'POST'
    },
    { manual: true }
  );

  const validation = (values: IReplaceDeviceForm) => {
    const errors: Record<string, string> = {};
    if (!values.condition) errors.condition = formatMessage({ id: 'FieldNotEmptyKey' });
    if (isSmartMeter) {
      if (!values.imei) errors.imei = formatMessage({ id: 'FieldNotEmptyKey' });
      if (!values.trackingNumber) errors.trackingNumber = formatMessage({ id: 'FieldNotEmptyKey' });
    }
    if (!values.replacementDeviceOrderingId) {
      errors.replacementDeviceOrderingId = formatMessage({ id: 'FieldNotEmptyKey' });
    }
    return errors;
  };

  const handleSubmit = async (values: IReplaceDeviceForm) => {
    const data: IReplaceDevicePayload = {
      condition: values.condition,
      replacementDeviceOrderingId: values.replacementDeviceOrderingId!!
    };
    if (isSmartMeter) {
      data.imei = values.imei;
      data.trackingNumber = values.trackingNumber;
    }
    data.shippingInfo = toDeviceOrderShippingInfo(values.shippingInfo);
    if (values.shippingInfo.useAsPatientAddress) {
      data.shippingAddressIsPatientAddress = true;
    }
    if (isAmazonFulfilled(shippingProviderFor(values.replacementDeviceOrderingId))) {
      data.shippingSpeed = values.shippingSpeed;
    }
    await post({ data });
    onSuccess();
    onClose();
  };

  return (
    <Modal show onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{formatMessage({ id: 'replaceDeviceKey' })}</Modal.Title>
      </Modal.Header>
      {loadingPatient || loadingReplacementOptions ? (
        <Modal.Body>
          <Loading />
        </Modal.Body>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validate={validation}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({ values }) => {
            const currentAddressValid = Boolean(isAddressValid(values.shippingInfo.address));
            const replacementIsAmazon = isAmazonFulfilled(
              shippingProviderFor(values.replacementDeviceOrderingId)
            );
            return (
              <FormikForm>
                <Modal.Body>
                  {loading && <Loading />}
                  {error && <Error retry={post} />}
                  {!currentAddressValid && (
                    <Alert variant="warning">
                      {formatMessage({ id: 'addressRequiredForReplacementKey' })}
                    </Alert>
                  )}
                  {skuOptions.length === 0 && (
                    <Alert variant="danger">
                      {formatMessage({ id: 'noReplacementDevicesAvailableKey' })}
                    </Alert>
                  )}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-group">
                        <Select
                          dataTestId="replace-device-condition"
                          label={formatMessage({ id: 'selectReplaceReasonKey' })}
                          options={conditionOptions}
                          variant="form"
                          name="condition"
                          isDisabled={!currentAddressValid}
                        />
                      </Form.Group>
                    </Col>
                    {skuOptions.length > 0 && (
                      <Col md={6}>
                        <Form.Group className="form-group">
                          <Select
                            dataTestId="replace-device-sku"
                            label={formatMessage({ id: 'selectReplacementSkuKey' })}
                            options={skuOptions}
                            variant="form"
                            name="replacementDeviceOrderingId"
                            isDisabled={!currentAddressValid}
                            required
                          />
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                  <ShippingAddressForm
                    gid={device.gid ?? ''}
                    showAddressToggle={showAddressToggle}
                    homeShippingCandidate={homeCandidate}
                    lastShippingCandidate={shippingCandidate}
                  />
                  <ShippingSpeedField
                    gid={device.gid ?? ''}
                    items={
                      values.replacementDeviceOrderingId
                        ? [{ deviceOrderingId: values.replacementDeviceOrderingId, quantity: 1 }]
                        : []
                    }
                    enabled={replacementIsAmazon}
                  />
                  {isSmartMeter && values.condition && (
                    <>
                      <Row>
                        <Col>
                          <Alert variant="primary">
                            {formatMessage({ id: 'replaceDeviceNoticeKey' })}
                          </Alert>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="form-group">
                            <Input
                              data-test="replace-device-imei"
                              type="text"
                              label={formatMessage({ id: 'imeiKey' })}
                              name="imei"
                              required
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="form-group">
                            <Input
                              data-test="replace-device-tracking-number"
                              type="text"
                              label={formatMessage({ id: 'trackingNumberKey' })}
                              name="trackingNumber"
                              required
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="link" onClick={onClose}>
                    {formatMessage({ id: 'Cancel' })}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={!currentAddressValid || skuOptions.length === 0}
                  >
                    {formatMessage({ id: 'replaceKey' })}
                  </Button>
                </Modal.Footer>
              </FormikForm>
            );
          }}
        </Formik>
      )}
    </Modal>
  );
};

export default ReplaceDeviceModal;
